import React, { useState, useCallback, useEffect } from 'react';
import CloseIcon from "../../assets/images/close.png";
import { connect } from "react-redux";
import { compose } from "redux";
import { Disable } from 'react-disable';
import debounce from "lodash/debounce";

import { 
    updateComplianceStandard, 
    addProcedureToCompliance, 
    addAttachmentToProcedure, 
    editComplianceProcedure, 
    getComplianceProcedures 
} from "../../store/actions/complianceActions";

import Select from 'react-select';

const customStyles = {
    control: (provided, state) => ({
        ...provided,
        borderColor: state.isFocused ? '#3b3b3b' : '#ccc',
        boxShadow: state.isFocused ? '0 0 0 1px #3b3b3b' : 'none',
        '&:hover': {
            borderColor: '#3b3b3b',
        },
        backgroundColor: '#EEF0ED',
        padding: 1,
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 999999999999,
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#3b3b3b' : state.isFocused ? '#EEF0ED' : 'white',
        color: state.isSelected ? 'white' : '#3b3b3b',
        '&:hover': {
            backgroundColor: '#EEF0ED',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#3b3b3b',
    }),
};

const ComplianceOverlay = ({ 
    close, 
    compliance, 
    edit, 
    updateComplianceFN, 
    compliance_redux, 
    addProcedureToComplianceFN, 
    addAttachmentToProcedureFN, 
    editComplianceProcedureFN, 
    getComplianceProceduresFN 
}) => {
    const [isLoading, setIsLoading] = useState(false);

    const compliance_categories = [
        { value: 'ISO', label: 'ISO', color: "#0081A7" },
        { value: 'NIST', label: 'NIST', color: "#00AFB9" },
        { value: 'GDPR', label: 'GDPR', color: "#FED9B7" },
        { value: 'PCI-DSS', label: 'PCI-DSS', color: "#D6606E" },
        { value: 'HIPAA', label: 'HIPAA', color: "#BB133E" },
    ];

    const compliance_colors = [
        { value: '#FF5733', label: 'Red Orange', color: "#FF5733" },
        { value: '#33FF57', label: 'Green', color: "#33FF57" },
        { value: '#3357FF', label: 'Blue', color: "#3357FF" },
        { value: '#F1C40F', label: 'Yellow', color: "#F1C40F" },
        { value: '#8E44AD', label: 'Purple', color: "#8E44AD" },
        { value: '#E67E22', label: 'Carrot', color: "#E67E22" },
        { value: '#2ECC71', label: 'Emerald', color: "#2ECC71" },
        { value: '#3498DB', label: 'Peter River', color: "#3498DB" },
        { value: '#9B59B6', label: 'Amethyst', color: "#9B59B6" },
        { value: '#34495E', label: 'Wet Asphalt', color: "#34495E" }
    ];

    const [policies, setPolicies] = useState([]);
    const [formControlState, setFormControlState] = useState([]);
    const [currentPolicy, setCurrentPolicy] = useState(null);
    const fileInputRef = React.useRef(null);

    const [formState, setFormState] = useState({
        name: compliance ? compliance.name : '',
        category: compliance ? compliance.category : '',
        description: compliance ? compliance.description : '',
        color: compliance ? compliance.color : '',
        comments: '',
    });

    useEffect(() => {
        if (compliance) {
            setPolicies([]);
            getComplianceProceduresFN(compliance.compliance_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (compliance) {
            setPolicies(compliance.policies);
        }
    }, [compliance]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file && currentPolicy) {
            addAttachmentToProcedureFN(compliance.compliance_id, currentPolicy.id, file);
        }
    };

    const handleAttachmentClick = (policy) => {
        setCurrentPolicy(policy);
        if (policy.attachment) {
            window.open(policy.attachment, '_blank');
        } else {
            fileInputRef.current.click();
        }
    };

    const handleChange = (data) => {
        setFormState(data);
        updateServerData(data, compliance.compliance_id);
    };

    const updateServerData = useCallback(
        debounce(async (data, compliance_id) => {
            ////console.log("Debounced update triggered for compliance:", compliance_id, data);
            setIsLoading(true);
            try {
                await updateComplianceFN(compliance_id, data); // Await the action to ensure completion
            } catch (error) {
                console.error("Error updating compliance standard:", error);
            } finally {
                setIsLoading(false); // Ensure isLoading is reset whether successful or not
            }
        }, 1500),
        []
    );

    const handleChangePolicy = (data, policy_id) => {
        const updatedPolicy = data.find(policy => policy.id === policy_id);
        updateServerData2(updatedPolicy, compliance.compliance_id, policy_id);
    };

    const updateServerData2 = useCallback(
        debounce((data, compliance_id, policy_id) => {
            setIsLoading(true);
            editComplianceProcedureFN(compliance_id, policy_id, data);
        }, 1500),
        []
    );

    useEffect(() => {
        if (compliance_redux) {
            const this_compliance = compliance_redux.find(
                complianceItem => complianceItem.id === compliance.compliance_id
            );
            setFormControlState([]);
            this_compliance &&
                this_compliance.policies.map(policy => {
                    const newPolicy = { ...policy };
                    setFormControlState(prevState => [...prevState, newPolicy]);
                });
            setIsLoading(false);
        }
    }, [compliance_redux, compliance.compliance_id]);

    return (
        <Disable disabled={isLoading}>
            <div className="p-6 bg-white rounded-lg shadow-2xl">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-1xl font-semibold text-[#3b3b3b]">
                        {edit ? "Edit Compliance" : "New Compliance"}
                    </h1>
                    <img
                        src={CloseIcon}
                        alt="Close"
                        className="w-8 h-8 cursor-pointer"
                        onClick={close}
                    />
                </div>

                <div className="mb-6">
                    <input
                        type="text"
                        placeholder="Name"
                        className="w-full p-3 border border-gray-300 rounded mb-4 bg-[#EEF0ED] text-black"
                        value={formState.name}
                        onChange={(e) => handleChange({ ...formState, name: e.target.value })}
                    />
                    <textarea
                        placeholder="Description"
                        className="w-full p-3 border border-gray-300 rounded bg-[#EEF0ED] text-black"
                        value={formState.description}
                        onChange={(e) => handleChange({ ...formState, description: e.target.value })}
                    />
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                    <Select
                        options={compliance_categories}
                        placeholder="Category"
                        onChange={(e) => handleChange({ ...formState, category: e.value })}
                        styles={customStyles}
                        value={compliance_categories.find(cat => cat.value === formState.category)}
                    />
                    <Select
                        options={compliance_colors} // Reuse or create colors specific to compliance
                        placeholder="Color"
                        onChange={(e) => handleChange({ ...formState, color: e.value })}
                        styles={customStyles}
                        value={compliance_categories.find(color => color.value === formState.color)}
                    />
                </div>

                {/* <div className="mb-4 flex justify-between items-center">
                    <h3 className="font-semibold mb-2 text-[#3b3b3b]">Procedures</h3>
                    <button
                        className="bg-[#3b3b3b] text-white py-2 px-4 rounded hover:bg-opacity-70"
                        onClick={() => addProcedureToComplianceFN(compliance.compliance_id)}
                    >
                        Add New Procedure
                    </button>
                </div> */}

                <div className="table w-full mb-6 border border-gray-300 rounded-lg bg-[#EEF0ED]">
                    <div className="table-row bg-[#EEF0ED]">
                        <div className="table-cell p-4 border-b border-gray-300 font-semibold">Number</div>
                        <div className="table-cell p-4 border-b border-gray-300 font-semibold">Details</div>
                        <div className="table-cell p-4 border-b border-gray-300 font-semibold">Attachment</div>
                    </div>
                    {formControlState.map((policy, index) => (
                        <div className="table-row bg-white" key={index}>
                            <div className="table-cell p-4 border-b border-gray-300">
                                <span className="font-semibold">{policy.number}</span>
                            </div>
                            <div className="table-cell p-4 border-b border-gray-300">
                                <input
                                    type="text"
                                    value={formControlState.find(c => c.id === policy.id)?.details || ''}
                                    className="w-[250px] p-2"
                                    onChange={(e) => {
                                        const policyId = policy.id;
                                        const updatedPolicies = [...formControlState];
                                        const policyToUpdate = updatedPolicies.find(p => p.id === policyId);
                                        if (policyToUpdate) {
                                            policyToUpdate.details = e.target.value;
                                        }
                                        handleChangePolicy(updatedPolicies, policyId);
                                    }}
                                />
                            </div>
                            <div className="table-cell p-4 border-b border-gray-300">
                                <button
                                    onClick={() => handleAttachmentClick(policy)}
                                    className="text-blue-500 hover:bg-opacity-70"
                                >
                                    {policy.attachment ? "View Attachment" : "No Attachment"}
                                </button>
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </Disable>
    );
};

const mapStateToProps = (state) => {
    return {
        compliance_redux: state.compliance.policies,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        updateComplianceFN: (compliance_id, data) => dispatch(updateComplianceStandard(compliance_id, data)),
        addProcedureToComplianceFN: (compliance_id) => dispatch(addProcedureToCompliance(compliance_id)),
        addAttachmentToProcedureFN: (compliance_id, policy_id, file) => dispatch(addAttachmentToProcedure(compliance_id, policy_id, file)),
        editComplianceProcedureFN: (compliance_id, policy_id, policy) => dispatch(editComplianceProcedure(compliance_id, policy_id, policy)),
        getComplianceProceduresFN: (compliance_id) => dispatch(getComplianceProcedures(compliance_id)),
    };
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(ComplianceOverlay);